import { useCallback, useEffect, useMemo, useState } from "react";
import { BaseProcedureEntity } from "../../../../application/domain/procedures/procedure-entity";
import apiClient from "../../../../application/apis/api-client";
import {
  DefaultNumItemsPerPages,
  paging,
  toTotalPage,
} from "../../../../application/domain/pagination/paging";
import {
  GrafferManagedTag,
  reportUnexpectedError,
} from "@graffer-inc/smart-apply";
import { toast } from "react-toastify";
import { SmartApplyMenuList } from "../../../organisms/smart-apply/menu-list";
import { SmartApplyTemplate } from "../../../template/smart-apply/default-template";
import {
  Table,
  TableHeader,
  Th,
  TableBody,
  TableRow,
  Td,
  Pagination,
  InformationBanner,
} from "@graffer-inc/console-design-kit";
import { Loader } from "../../../organisms/common/loading";
import { SmartApplyR } from "../route";
import { useHistory, useLocation } from "react-router-dom";
import { ArchivedProceduresListHeader } from "./contentsHeader";
import {
  parseQueryOfLocation,
  rebuildQuery,
} from "../../../../utils/url-utils";

export const ArchivedProceduresList: React.FC = () => {
  const history = useHistory();
  const [archivedProcedures, setArchivedProcedures] = useState<
    BaseProcedureEntity[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { page, name } = parseQueryOfLocation(useLocation()) as {
    page: string | undefined;
    name: string | undefined;
  };
  const [searchName, setSearchName] = useState<string>(name ?? "");
  const [paging, setPaging] = useState<paging>({
    current: 1,
    total: 0,
  });
  const ResourceFetchErrorMessages: {
    title: string;
    subtitle: string;
  } = useMemo(() => {
    return name === undefined || name === ""
      ? {
          title: "アーカイブ済みの手続きはありません。",
          subtitle: "アーカイブされた手続きが、ここに表示されます。",
        }
      : {
          title: "条件に該当する手続きはありませんでした。",
          subtitle: "検索文字列を確認してください。",
        };
  }, [name]);

  const fetchArchivedProcedures = useCallback(
    async (currentPage: number) => {
      setIsLoading(true);
      try {
        const { data: procs, headers } =
          await apiClient.fetchArchivedProcedures(
            currentPage,
            DefaultNumItemsPerPages,
            {
              name: name,
            },
          );
        setPaging({
          current: currentPage,
          total: toTotalPage(
            Number(headers["pagination-count"]),
            DefaultNumItemsPerPages,
          ),
        });
        setArchivedProcedures(procs);
      } catch (e) {
        reportUnexpectedError(e);
        toast.error("アーカイブ済み手続き一覧の取得に失敗しました。");
      } finally {
        setIsLoading(false);
      }
    },
    [name],
  );

  useEffect(() => {
    const currentPage = page ? parseInt(page) : 1;
    fetchArchivedProcedures(currentPage);
  }, [page, fetchArchivedProcedures]);

  const movePage = (page: number) => {
    history.push(
      rebuildQuery({
        page: page.toString(),
        name: searchName,
      }),
    );
  };

  const searchArchivedProcedures = (name: string | undefined) => {
    history.push(
      rebuildQuery({
        page: "1",
        name: name,
      }),
    );
  };

  return (
    <SmartApplyTemplate
      breadcrumbs={[{ to: "#", title: "手続き一覧" }]}
      menuList={<SmartApplyMenuList />}
      contentsHeader={
        <ArchivedProceduresListHeader
          searchName={searchName}
          setSearchName={setSearchName}
          searchArchivedProcedures={searchArchivedProcedures}
        />
      }
    >
      <div className="procedures">
        <Table borderType="row" wrapper={false}>
          <TableHeader>
            <Th>手続き名称</Th>
            <Th>手続きID</Th>
          </TableHeader>
          <TableBody>
            <Loader isLoading={isLoading}>
              {archivedProcedures.map((a) => (
                <TableRow
                  key={a.id}
                  onClick={() =>
                    history.push(SmartApplyR.ProcedureDetail.link({ id: a.id }))
                  }
                >
                  <Td>
                    {a.name}
                    {a.isGrafferManaged && (
                      <>
                        &nbsp; &nbsp;
                        <GrafferManagedTag />
                      </>
                    )}
                  </Td>
                  <Td>{a.id}</Td>
                </TableRow>
              ))}
            </Loader>
          </TableBody>
        </Table>
        {!isLoading && archivedProcedures.length === 0 && (
          <div className="py-2">
            <InformationBanner
              message={ResourceFetchErrorMessages.title}
              description={ResourceFetchErrorMessages.subtitle}
            />
          </div>
        )}
        {paging.total > 0 && (
          <div className="procedures-pagination-footer">
            <Pagination
              onPageClick={movePage}
              currentPage={paging.current}
              lastPage={paging.total}
            />
          </div>
        )}
      </div>
    </SmartApplyTemplate>
  );
};
